import React from "react"

import { ReactComponent as StudentIcon } from "@brand_assets/svg/student_icon.svg"
import { Box, Button, Stack, Typography } from "@mui/material"
import { useMutationAppleLoginStudent } from "api/reactQuery/mutations/appleLogin"
import { useMutationGoogleLoginStudent } from "api/reactQuery/mutations/googleLogin"
import { useQueryStudentCleverLogin } from "api/reactQuery/queries/studentCleverLogin"
import { AxiosError } from "axios"
import AppleButton from "components/common/appleButton"
import CleverButton from "components/common/button/cleverButton"
import CleverRestrictedTakeover from "components/common/cleverRestrictedTakeover"
import CleverSSOLoader from "components/common/cleverSSOLoader"
import GoogleButton from "components/common/googleButton"
import OpenIdSSOLoaders from "components/common/openIdSSOLoaders"
import RegisterInformation from "components/common/registerInformation"
import { useApple } from "hooks/apple"
import { useCleverRestrictedError } from "hooks/cleverRestrictedError/useCleverRestrictedError"
import { useCleverSSO } from "hooks/cleverSSO/useCleverSSO"
import { useGoogle } from "hooks/google"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppDispatch } from "hooks/store"
import { useTranslation } from "react-i18next"
import { setActivePageSignUp, setAuthData } from "store/auth/auth.slice"
import { UserRole } from "ts/enums/User"
import { getErrorMessageFromTab } from "utils/api"

import SignByEmailButton from "./signByEmailButton"

const SignAsStudent = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { showSnackbar } = useCustomSnackbar()
  const { isCleverSSOLoading, handleCleverReset, handleCleverAuthSuccess } =
    useCleverSSO({ userType: "student", isSignup: true })
  const { isLocked, handleCleverRestrictedError, resetLock } =
    useCleverRestrictedError()

  const handleSSOSuccess = (data: {
    data: { access_token: string; finalization_required: boolean }
  }) => {
    dispatch(
      setAuthData({
        token: data.data.access_token,
        role: UserRole.STUDENT,
        finalized: !data.data.finalization_required,
      }),
    )

    if (!data.data.finalization_required) {
      showSnackbar({ title: t("noAuthFlow.loginSuccessful") })
    } else {
      showSnackbar({ title: t("noAuthFlow.yourAccountHasBeenCreated") })
    }

    if (data.data.finalization_required) {
      dispatch(setActivePageSignUp(3))
    }
  }

  const handleSSOError = (error: AxiosError) => {
    if (handleCleverRestrictedError(error)) return
    showSnackbar({ title: getErrorMessageFromTab(error), variant: "error" })
  }

  const { mutate } = useMutationGoogleLoginStudent({
    option: { onSuccess: handleSSOSuccess, onError: handleSSOError },
  })

  const { mutate: appleLogin } = useMutationAppleLoginStudent({
    option: { onSuccess: handleSSOSuccess, onError: handleSSOError },
  })

  const { refButton } = useGoogle({ mutate })

  const { signIn: appleSignIn } = useApple({ mutate: appleLogin })

  const { refetch: cleverSignup } = useQueryStudentCleverLogin()

  return (
    <>
      <CleverSSOLoader
        isLoading={isCleverSSOLoading}
        userType="student"
        onReset={handleCleverReset}
        onAuthSuccess={handleCleverAuthSuccess}
      />
      <OpenIdSSOLoaders userType="student" isSignup />
      {isLocked ? (
        <CleverRestrictedTakeover
          icon={StudentIcon}
          onSignInWithClever={() => cleverSignup()}
          onReset={resetLock}
        />
      ) : (
        <Stack>
          <Stack textAlign="left">
            <Typography
              variant="h2"
              component="p"
              color="primary.main"
              fontWeight="600"
            >
              {t("noAuthFlow.student")}
            </Typography>
            <Typography
              component="h1"
              tabIndex={-1}
              variant="body1"
              fontSize="24px"
              fontWeight="500"
              color="mockup.neutral50"
              sx={{ pt: 1, pb: 4 }}
            >
              {t("noAuthFlow.signup")}
            </Typography>
          </Stack>
          <GoogleButton text={t("noAuthFlow.signUpGoogle")} ref={refButton} />
          <Box sx={{ mt: 2 }}>
            <AppleButton
              text={t("noAuthFlow.signUpApple")}
              onClick={appleSignIn}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <CleverButton
              text="SIGN UP WITH CLEVER"
              onClick={() => {
                cleverSignup()
              }}
            />
          </Box>
          <Typography variant="subtitle1" color="mockup.grey80" py={2}>
            {t("defaultTranslations.or")}
          </Typography>
          <SignByEmailButton onClick={() => dispatch(setActivePageSignUp(2))} />
          <Button
            variant="outlined"
            fullWidth
            sx={{ mt: 6 }}
            onClick={() => (window.location.href = "https://www.classbank.com/")}
          >
            {t("noAuthFlow.back")}
          </Button>
          <RegisterInformation isSignInOrUp />
        </Stack>
      )}
    </>
  )
}

export default SignAsStudent
